/**
 * Parlay Filters — Shared filter/sort logic for AI-generated picks
 *
 * Used by both web (PicksView) and mobile (PicksScreen) to filter and sort
 * generated parlay recommendations by confidence, prop type, player name,
 * and various sort criteria.
 */

import {
  getCategoryLabel,
  marketKeyToCategory,
  PROP_TYPE_FILTER_OPTIONS,
  type PropCategory,
} from './propTypes';
import {
  comboComponents,
  getEffectiveStatType,
  type StatKey,
  statKeyParts,
  statTypeToMarketKey,
} from './statType';

// =============================================================================
// Types
// =============================================================================

export interface PicksFilter {
  confidence: string;
  propType: string;
  playerSearch: string;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

export const DEFAULT_PICKS_FILTER: PicksFilter = {
  confidence: 'all',
  propType: 'all',
  playerSearch: '',
  sortBy: 'default',
  sortDirection: 'desc',
};

/**
 * Minimal parlay shape required for filtering.
 * Both web's Parlay type and mobile's GeneratedParlay satisfy this constraint
 * via their index signatures or explicit fields.
 */
interface FilterableLeg {
  player?: string | null;
  playerName?: string | null;
  prop?: string | null;
  statType?: string | null;
  marketKey?: string | null;
}

export interface FilterableParlay {
  confidence?: string | null;
  totalOdds?: string | number | null;
  odds?: string | number | null;
  rank?: number;
  name?: string;
  legs?: FilterableLeg[] | null;
  [key: string]: unknown;
}

// =============================================================================
// Constants
// =============================================================================

/** Numeric order for confidence-based sorting (higher = better). */
export const CONFIDENCE_ORDER: Record<string, number> = {
  high: 3,
  medium: 2,
  moderate: 2,
  low: 1,
};

/** Available confidence filter options for UI rendering. */
export const CONFIDENCE_OPTIONS = ['all', 'high', 'moderate', 'low'] as const;

/** Available prop type filter options for UI rendering. */
export const PROP_TYPE_OPTIONS = [
  'all',
  'points',
  'rebounds',
  'assists',
  'threes',
  'combos',
] as const;

/** The prop type options with their labels: the shared category names, and 'All' for no filter. */
export const PROP_TYPE_CHOICES = PROP_TYPE_OPTIONS.map((key) => ({
  key,
  label: key === 'all' ? 'All' : getCategoryLabel(key),
}));

/** Available sort-by options for UI rendering. */
export const SORT_BY_OPTIONS = ['default', 'confidence', 'odds', 'legs'] as const;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Parse American odds string/number to a numeric value for comparison.
 * Strips leading "+", commas, and other non-numeric chars.
 */
export function parseOdds(odds?: string | number | null): number {
  if (odds === undefined || odds === null) return 0;
  const cleaned = odds.toString().replace(/[+,]/g, '');
  const val = parseInt(cleaned, 10);
  return Number.isNaN(val) ? 0 : val;
}

/**
 * Get the player name from a leg, handling both web (player) and mobile (playerName) shapes.
 */
function getLegPlayer(leg: { player?: string | null; playerName?: string | null }): string {
  return leg.player || leg.playerName || '';
}

function isPropCategory(value: string): value is PropCategory {
  return PROP_TYPE_FILTER_OPTIONS.some((option) => option.key !== '' && option.key === value);
}

/** Whether a leg belongs to a prop type filter, judged by its stat key rather than its text. */
function legMatchesPropType(leg: FilterableLeg, propType: string): boolean {
  // The box-score stat a category asks for; a combo containing it counts too.
  const statForCategory: Partial<Record<string, StatKey>> = {
    points: 'pts',
    rebounds: 'reb',
    assists: 'ast',
    threes: 'threes',
    steals: 'stl',
    blocks: 'blk',
    turnovers: 'turnover',
    field_goals: 'fgm',
    free_throws: 'ftm',
  };
  const statKey = getEffectiveStatType(leg);
  if (propType === 'combos') return comboComponents(statKey) !== null;
  const stat = statForCategory[propType];
  if (stat) return statKeyParts(statKey).includes(stat);
  if (!isPropCategory(propType)) return true;
  const marketKey = leg.marketKey || statTypeToMarketKey(statKey, leg.prop);
  return marketKey !== null && marketKeyToCategory(marketKey) === propType;
}

// =============================================================================
// Filter Function
// =============================================================================

/**
 * Filter and sort an array of parlays based on the given filter criteria.
 *
 * Generic over T so callers retain their specific parlay type (web Parlay,
 * mobile GeneratedParlay, etc.) in the returned array.
 */
export function filterParlays<T extends FilterableParlay>(recs: T[], filters: PicksFilter): T[] {
  let result = [...recs];

  // -- Confidence filter --
  if (filters.confidence !== 'all') {
    result = result.filter((parlay) => {
      const conf = parlay.confidence?.toLowerCase();
      switch (filters.confidence) {
        case 'high':
          return conf === 'high';
        case 'moderate':
          return conf === 'medium' || conf === 'moderate';
        case 'low':
          return conf === 'low';
        default:
          return true;
      }
    });
  }

  // -- Prop type filter --
  if (filters.propType !== 'all') {
    result = result.filter((parlay) =>
      parlay.legs?.some((leg) => legMatchesPropType(leg, filters.propType)),
    );
  }

  // -- Player search filter --
  if (filters.playerSearch.trim()) {
    const query = filters.playerSearch.toLowerCase().trim();
    result = result.filter((parlay) => {
      return parlay.legs?.some((leg) => getLegPlayer(leg).toLowerCase().includes(query));
    });
  }

  // -- Sort --
  if (filters.sortBy !== 'default') {
    const direction = filters.sortDirection === 'asc' ? -1 : 1;
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'odds':
          comparison = parseOdds(b.totalOdds ?? b.odds) - parseOdds(a.totalOdds ?? a.odds);
          break;
        case 'confidence':
          comparison =
            (CONFIDENCE_ORDER[b.confidence?.toLowerCase() || ''] || 0) -
            (CONFIDENCE_ORDER[a.confidence?.toLowerCase() || ''] || 0);
          break;
        case 'legs':
          comparison = (b.legs?.length || 0) - (a.legs?.length || 0);
          break;
        default:
          return 0;
      }
      return comparison * direction;
    });
  }

  return result;
}

/**
 * Count how many non-default filters are currently active.
 * Useful for showing a badge count in filter UI.
 */
export function countActiveFilters(filters: PicksFilter): number {
  let count = 0;
  if (filters.confidence !== DEFAULT_PICKS_FILTER.confidence) count++;
  if (filters.propType !== DEFAULT_PICKS_FILTER.propType) count++;
  if (filters.playerSearch.trim() !== '') count++;
  if (filters.sortBy !== DEFAULT_PICKS_FILTER.sortBy) count++;
  return count;
}
